import type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnPinningState,
  PaginationState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import produce from 'immer';
import { mergeWith } from 'lodash-es';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type ReactTableState = {
  columnFilters: ColumnFiltersState;
  columnOrder: ColumnOrderState;
  columnPinning: ColumnPinningState;
  columnVisibility: VisibilityState;
  isGroupByParent: boolean;
  globalFilter: string;
  pagination: PaginationState;
  sorting: SortingState;
};

type ListingPagesState = {
  listingPagesState: {
    [entityName: string]: ReactTableState;
  };
  setListingPagesState: (entityName: string) => (newListingPageState: Partial<ReactTableState>) => void;
  /** Clear session state (filters, search) in-memory and sessionStorage. Call on logout. */
  clearSessionState: () => void;
};

const mergeArrayOfColumnFiltersCustomizer = (objValue: any, srcValue: any, key: string) => {
  if (Array.isArray(objValue)) {
    return srcValue;
  }
  // For columnVisibility, replace entirely instead of merging to ensure new reference
  if (key === 'columnVisibility' && srcValue !== undefined) {
    return srcValue;
  }
};

export const useListingPagesStore = create<ListingPagesState>()(
  persist(
    (set) => ({
      listingPagesState: {},
      setListingPagesState: (pageKey) => (newState) =>
        set(
          produce(({ listingPagesState }) => {
            listingPagesState[pageKey] = mergeWith(
              listingPagesState[pageKey],
              newState,
              mergeArrayOfColumnFiltersCustomizer,
            );
          }),
        ),
      clearSessionState: () =>
        set(
          produce(({ listingPagesState }) => {
            for (const key of Object.keys(listingPagesState)) {
              const page = listingPagesState[key];
              if (page) {
                delete page.columnFilters;
                delete page.globalFilter;
                if (page.pagination) {
                  page.pagination.pageIndex = 0;
                }
              }
            }
          }),
        ),
    }),
    {
      name: 'listing-prefs',
      partialize: (state) => ({
        listingPagesState: Object.fromEntries(
          Object.entries(state.listingPagesState).map(([key, val]) => [
            key,
            {
              columnVisibility: val.columnVisibility,
              columnOrder: val.columnOrder,
              isGroupByParent: val.isGroupByParent,
              sorting: val.sorting,
              pagination: val.pagination ? { pageSize: val.pagination.pageSize } : undefined,
            },
          ]),
        ),
      }),
    },
  ),
);
